"use client";

import { useMemo, useState, useEffect } from "react";
import { useParams } from "next/navigation";
import TFLink from "@/Web/Components/TFLink/TFLink";
import TFLayoutHomeSection from "@/Web/Components/TFLayoutHomeSection/TFLayoutHomeSection";
import TFBadge from "@/Web/Components/TFBadge/TFBadge";
import TFHorizontalSlider from "@/Web/Components/TFHorizontalSlider/TFHorizontalSlider";
import apiCall from "@/libs/api";
import { HiCalendar, HiLocationMarker } from "react-icons/hi";
import { useTranslations } from "next-intl";

const EventCard = ({
  event,
  href,
}: {
  event: APIDataSpacesEvent;
  href: string;
}) => {
  const t = useTranslations("web.pages.home.sections.events");
  const eventDate = new Date(event.startDate);
  const formattedDate = eventDate.toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "short",
  });

  return (
    <TFLink
      href={href}
      className="bg-black/5 rounded-xl p-4 hover:bg-black/10 transition-colors h-full flex flex-col group"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex flex-wrap gap-2">
          <TFBadge title={(event.category as any).title} />
        </div>
      </div>

      <h3 className="text-base font-semibold text-black mb-2 line-clamp-2 group-hover:text-black/70 transition-colors">
        {event.title}
      </h3>
      <p className="text-sm text-black/60 mb-4 line-clamp-3 flex-grow">
        {event.description}
      </p>

      <div className="flex flex-col gap-2 text-xs text-black/50">
        <div className="flex items-center gap-2">
          <HiCalendar className="w-4 h-4" />
          <span className="font-medium">{formattedDate}</span>
        </div>
        {event.location.address && (
          <div className="flex items-center gap-2">
            <HiLocationMarker className="w-4 h-4" />
            <span className="line-clamp-1">{event.location.address}</span>
            {event.isOnline && (
              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                {t("online")}
              </span>
            )}
          </div>
        )}
      </div>
    </TFLink>
  );
};

export default function SectionEvents() {
  const t = useTranslations("web.pages.home.sections.events");
  const params = useParams<{ locale: string }>();
  const locale = params?.locale || "ru";
  const [itemsPerView, setItemsPerView] = useState(3);
  const [events, setEvents] = useState<APIDataSpacesEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const updateItemsPerView = () => {
      setItemsPerView(window.innerWidth < 768 ? 1 : 3);
    };

    updateItemsPerView();
    window.addEventListener("resize", updateItemsPerView);
    return () => window.removeEventListener("resize", updateItemsPerView);
  }, []);

  useEffect(() => {
    const loadEvents = async () => {
      try {
        setIsLoading(true);
        const response = await apiCall("hub/spaces/events", {
          action: "publicList",
          locale: locale as UILocale,
        });
        setEvents((response as any).events || []);
      } catch (error) {
        console.error("Failed to load events:", error);
        setEvents([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (locale) {
      loadEvents();
    }
  }, [locale]);

  const featuredEvents = useMemo(() => {
    const now = new Date();
    return (
      [...events]
        // TODO: Uncomment this when events are live
        // .filter((event) => new Date(event.startDate) >= now)
        .sort((a, b) => {
          return (
            new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
          );
        })
        .slice(0, 6)
    );
  }, [events]);

  const totalEvents = useMemo(() => {
    return events.length;
  }, [events]);

  const upcomingEvents = useMemo(() => {
    const now = new Date();
    return events.filter((event) => new Date(event.startDate) >= now).length;
  }, [events]);

  if (isLoading || featuredEvents.length === 0) {
    return null;
  }

  return (
    <TFLayoutHomeSection
      moreButton={{
        title: t("allEvents"),
        href: "/events",
      }}
      sideInfo={{
        title: t("title"),
        description: t("description"),
        statistics: [
          { title: t("totalEvents"), value: totalEvents },
          { title: t("upcoming"), value: upcomingEvents },
        ],
        position: "left",
      }}
      className="bg-black/2"
    >
      <TFHorizontalSlider
        items={featuredEvents}
        renderItem={(event) => (
          <EventCard event={event} href={`/events/${event.ref}`} />
        )}
        gap={16}
        autoScroll={false}
        showControls={true}
        showDots={true}
        itemsPerView={itemsPerView}
      />
    </TFLayoutHomeSection>
  );
}
